import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { Button, Card, LoadingState } from '@/components/ui'
import { Globe, Building2, Sliders, CheckCircle2 } from 'lucide-react'
import { useTenantStore } from '@/stores/tenants.ts'
import type { ArticleGroup } from '../shared.ts'

interface DefaultsResponse {
  salesMarginPct: number
  defaultItemGroupCode: string | null
  source: { salesMarginPct: 'tenant' | 'global' | 'builtin'; defaultItemGroupCode: 'tenant' | 'global' | 'builtin' }
  tenantId: string | null
}

/**
 * Pflegt die zentralen Defaults fuer Artikel-Neuanlage:
 *  - VK-Marge (in Prozent), zur Berechnung von VK = EK × (1 + Marge%)
 *  - Default-Artikelgruppe, die bei Neuanlage vorausgewaehlt wird
 *
 * Beide Werte koennen GLOBAL gepflegt werden (greifen fuer alle Mandanten) und
 * pro Mandant ueberschrieben werden (greift nur fuer den aktiven Mandanten).
 * Die Aufloesung „Tenant → Global → Built-in" passiert serverseitig in
 * GET /api/articles/defaults.
 */
export function DefaultsSection() {
  const queryClient = useQueryClient()
  const activeTenantId = useTenantStore((s) => s.activeTenantId)
  const tenants = useTenantStore((s) => s.tenants)
  const activeTenant = tenants.find((t) => t.id === activeTenantId)

  const { data: defaults, isLoading } = useQuery<DefaultsResponse>({
    queryKey: ['articles-defaults', activeTenantId],
    queryFn: () => apiClient.get('/articles/defaults').then((r) => r.data),
  })

  const { data: groups = [] } = useQuery<ArticleGroup[]>({
    queryKey: ['article-groups'],
    queryFn: () => apiClient.get('/articles/groups').then((r) => r.data),
  })

  // Globale Form-State + Tenant-Override-Form-State separat
  const [globalMargin, setGlobalMargin] = useState<string>('')
  const [globalGroupCode, setGlobalGroupCode] = useState<string>('')
  const [tenantMargin, setTenantMargin] = useState<string>('')
  const [tenantGroupCode, setTenantGroupCode] = useState<string>('')
  const [savedSection, setSavedSection] = useState<'global' | 'tenant' | null>(null)

  // Beim Laden zeigen wir die effektiv aufgeloesten Werte. Tenant-Felder sind
  // leer wenn kein Override existiert (Source = 'global' oder 'builtin').
  useEffect(() => {
    if (!defaults) return
    // Globale Werte: nur befuellen, wenn die Quelle 'global' ist — sonst leer
    // lassen damit User sieht „greift built-in default 30%".
    if (defaults.source.salesMarginPct === 'global') {
      setGlobalMargin(String(defaults.salesMarginPct))
    }
    if (defaults.source.defaultItemGroupCode === 'global' && defaults.defaultItemGroupCode) {
      setGlobalGroupCode(defaults.defaultItemGroupCode)
    }
    if (defaults.source.salesMarginPct === 'tenant') {
      setTenantMargin(String(defaults.salesMarginPct))
    }
    if (defaults.source.defaultItemGroupCode === 'tenant' && defaults.defaultItemGroupCode) {
      setTenantGroupCode(defaults.defaultItemGroupCode)
    }
  }, [defaults])

  const saveMutation = useMutation({
    mutationFn: async (body: { scope: 'global' | 'tenant'; salesMarginPct?: number | null; defaultItemGroupCode?: string | null }) => {
      return apiClient.put('/articles/defaults', body).then((r) => r.data)
    },
    onSuccess: (_data, variables) => {
      setSavedSection(variables.scope)
      queryClient.invalidateQueries({ queryKey: ['articles-defaults'] })
      setTimeout(() => setSavedSection(null), 2500)
    },
  })

  if (isLoading) return <LoadingState />

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-start gap-3 rounded-lg border border-brand-100 bg-brand-50/50 p-4">
        <Sliders className="w-5 h-5 text-brand-700 flex-shrink-0 mt-0.5" />
        <div className="text-small text-gray-700">
          <p className="font-medium text-gray-900 mb-1">
            Defaults für Artikel-Neuanlage in Angeboten
          </p>
          <p>
            Diese Werte greifen, wenn die KI im Angebotsdialog einen neuen
            Artikel anlegt. Die VK-Marge bestimmt, wie VK aus EK berechnet wird;
            die Default-Artikelgruppe wird beim Anlegen automatisch vorausgewählt.
            Reihenfolge: Mandanten-Override → global → eingebauter Default (30%).
          </p>
        </div>
      </div>

      {/* ── Global ───────────────────────────────────────────── */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-gray-500" />
            <div>
              <h3 className="text-h4 text-gray-900">Global (für alle Mandanten)</h3>
              <p className="text-caption text-gray-500">Greift, wenn der aktuelle Mandant keinen eigenen Override hat.</p>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-small font-medium text-gray-700 mb-1 block">
                VK-Marge (%)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={globalMargin}
                onChange={(e) => setGlobalMargin(e.currentTarget.value)}
                placeholder="z.B. 30"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <p className="text-caption text-gray-500 mt-1">
                Leer = eingebauter Default 30 %
              </p>
            </div>
            <div>
              <label className="text-small font-medium text-gray-700 mb-1 block">
                Default-Warengruppe (SAP-Code)
              </label>
              <select
                value={globalGroupCode}
                onChange={(e) => setGlobalGroupCode(e.currentTarget.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">(keine)</option>
                {groups.map((g) => (
                  <option
                    key={g.id}
                    value={String((g as unknown as { sapGroupCode?: number | string }).sapGroupCode ?? '')}
                  >
                    {g.name} ({(g as unknown as { sapGroupCode?: number | string }).sapGroupCode ?? '—'})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 mt-4">
            {savedSection === 'global' && (
              <span className="inline-flex items-center gap-1 text-small text-emerald-700">
                <CheckCircle2 className="w-4 h-4" />
                Global gespeichert
              </span>
            )}
            <Button
              variant="primary"
              size="sm"
              loading={saveMutation.isPending && saveMutation.variables?.scope === 'global'}
              onClick={() =>
                saveMutation.mutate({
                  scope: 'global',
                  salesMarginPct: globalMargin.trim() === '' ? null : Number(globalMargin),
                  defaultItemGroupCode: globalGroupCode === '' ? null : globalGroupCode,
                })
              }
            >
              Global speichern
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* ── Mandant-Override ─────────────────────────────────── */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-500" />
            <div>
              <h3 className="text-h4 text-gray-900">
                Override für „{activeTenant?.code ?? '?'} – {activeTenant?.name ?? ''}"
              </h3>
              <p className="text-caption text-gray-500">
                Greift nur für diesen Mandanten; lässt globale Werte für alle anderen unverändert.
              </p>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {!activeTenantId ? (
            <p className="text-small text-amber-700">
              Kein Mandant aktiv — bitte oben rechts einen Mandanten wählen.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-small font-medium text-gray-700 mb-1 block">
                    VK-Marge (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={tenantMargin}
                    onChange={(e) => setTenantMargin(e.currentTarget.value)}
                    placeholder="leer = global verwenden"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="text-small font-medium text-gray-700 mb-1 block">
                    Default-Warengruppe (SAP-Code)
                  </label>
                  <select
                    value={tenantGroupCode}
                    onChange={(e) => setTenantGroupCode(e.currentTarget.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">(global verwenden)</option>
                    {groups.map((g) => (
                      <option
                        key={g.id}
                        value={String((g as unknown as { sapGroupCode?: number | string }).sapGroupCode ?? '')}
                      >
                        {g.name} ({(g as unknown as { sapGroupCode?: number | string }).sapGroupCode ?? '—'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 mt-4">
                {savedSection === 'tenant' && (
                  <span className="inline-flex items-center gap-1 text-small text-emerald-700">
                    <CheckCircle2 className="w-4 h-4" />
                    Override gespeichert
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setTenantMargin('')
                    setTenantGroupCode('')
                    saveMutation.mutate({
                      scope: 'tenant',
                      salesMarginPct: null,
                      defaultItemGroupCode: null,
                    })
                  }}
                >
                  Override entfernen
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  loading={saveMutation.isPending && saveMutation.variables?.scope === 'tenant'}
                  onClick={() =>
                    saveMutation.mutate({
                      scope: 'tenant',
                      salesMarginPct: tenantMargin.trim() === '' ? null : Number(tenantMargin),
                      defaultItemGroupCode: tenantGroupCode === '' ? null : tenantGroupCode,
                    })
                  }
                >
                  Override speichern
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* ── Effektiv aufgeloeste Werte ──────────────────────────── */}
      {defaults && (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-caption text-gray-600 space-y-1">
          <p className="font-medium text-gray-700">Aktuell effektiv (für deinen aktiven Mandanten):</p>
          <p>
            VK-Marge: <span className="font-semibold text-gray-900">{defaults.salesMarginPct}%</span>{' '}
            <span className="text-gray-400">(Quelle: {defaults.source.salesMarginPct})</span>
          </p>
          <p>
            Default-Warengruppe (SAP):{' '}
            <span className="font-semibold text-gray-900">
              {defaults.defaultItemGroupCode ?? '(keine)'}
            </span>{' '}
            <span className="text-gray-400">(Quelle: {defaults.source.defaultItemGroupCode})</span>
          </p>
        </div>
      )}
    </div>
  )
}
